import { useCallback, useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { resolveDeclensionTranslationKey, TranslatableText, useTranslate, ValidationRules } from '@dreryk/react-common';

import { medicineTranslationKeys } from '~libs.desktop.shared.medicine';
import { DosageTimeUnit } from '~libs.shared.prescription';

import { ExtendedDosageFormBean, ExtendedDosageFormController } from '../ExtendedDosageForm.types';

import { StageFieldName, triggerStageField } from './StageFieldList.utils';

type UseStageFieldValidationRulesOptions = Pick<ExtendedDosageFormController, 'trigger'> & {
   name: StageFieldName;
   invalid: boolean;
};

export const useStageFieldValidationRules = (
   options: UseStageFieldValidationRulesOptions
): ValidationRules<ExtendedDosageFormBean> => {
   const { trigger, name, invalid } = options;

   useUpdateEffect(() => {
      triggerStageField(trigger, name);
   }, [invalid, trigger, name]);

   return useMemo(() => ({ required: true, validate: () => !invalid }), [invalid]);
};

type UseDosagePeriodTimeUnitLabelsOptions = Pick<ExtendedDosageFormController, 'watch'> & {
   amountName: StageFieldName;
   amountDefaultValue: number;
};

type DosagePeriodTimeUnitLabels = {
   timeUnitLabel: (unit: DosageTimeUnit) => TranslatableText;
   timeUnitLabelExtractor: (unit: DosageTimeUnit) => string;
};

export const useDosagePeriodTimeUnitLabels = (
   options: UseDosagePeriodTimeUnitLabelsOptions
): DosagePeriodTimeUnitLabels => {
   const translate = useTranslate();
   const amount = Number(options.watch(options.amountName) ?? options.amountDefaultValue);

   const timeUnitLabel = useCallback(
      (unit: DosageTimeUnit): TranslatableText => {
         const forms = medicineTranslationKeys.extendedDosage.timeUnitMap[unit];

         return resolveDeclensionTranslationKey(amount, forms.accusative, forms.genitive, forms.plural);
      },
      [amount]
   );

   const timeUnitLabelExtractor = useCallback((unit: DosageTimeUnit): string => translate(timeUnitLabel(unit)) ?? '', [
      translate,
      timeUnitLabel,
   ]);

   return { timeUnitLabel: timeUnitLabel, timeUnitLabelExtractor: timeUnitLabelExtractor };
};
